// @greenlight-spec: 28-it-dashboard-ui.md#compliance--soc-2-evidence
// @greenlight-spec: 10-security-compliance.md#compliance-evidence-not-compliance-bundles
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';

import { buildGdprCsv, buildGdprJson } from '@/components/compliance/exportGdpr';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { downloadTextFile } from '@/lib/download';
import type { GdprControlRow, GdprEvidence, GdprIntegrationEvidence } from '@greenlight/contract';

const CHAPTER_LABEL: Record<GdprControlRow['chapter'], string> = {
  transfers: 'Transfers',
  security: 'Security of processing',
  accountability: 'Accountability',
};

function residencyVariant(approved: boolean): StatusBadgeVariant {
  return approved ? 'active' : 'failed';
}

function ControlRow({ row }: { readonly row: GdprControlRow }): React.JSX.Element {
  return (
    <li className="flex flex-col gap-2 py-4 md:flex-row md:gap-6">
      <div className="md:w-44 md:shrink-0">
        <MonoValue>{row.control}</MonoValue>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-foreground">{row.title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{row.mechanism}</p>
        {row.policy_ids.length > 0 && (
          <p className="mt-2 flex flex-wrap gap-1.5">
            {row.policy_ids.map((id) => (
              <MonoValue key={id}>{id}</MonoValue>
            ))}
          </p>
        )}
      </div>
      <div className="flex items-start gap-2 md:w-44 md:shrink-0 md:justify-end">
        <StatusBadge variant={row.coverage === 'enforced' ? 'active' : 'inactive'}>
          {row.coverage === 'enforced'
            ? 'Enforced at merge'
            : row.coverage === 'reported'
              ? 'Reported, not blocking'
              : 'Recorded'}
        </StatusBadge>
        <Link
          to={row.evidence_href}
          className="text-[12px] font-medium text-link underline-offset-4 hover:underline"
        >
          Evidence
        </Link>
      </div>
    </li>
  );
}

function IntegrationRow({ row }: { readonly row: GdprIntegrationEvidence }): React.JSX.Element {
  return (
    <li className="flex flex-col gap-1.5 py-3 md:flex-row md:items-center md:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{row.display_name}</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          <MonoValue>{row.name}</MonoValue>
          {row.region !== '' && (
            <>
              {' '}
              · <MonoValue>{row.region}</MonoValue>
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 md:w-64 md:justify-end">
        <StatusBadge variant={residencyVariant(row.residency_recorded)}>
          {row.residency_recorded ? 'Residency recorded' : 'No residency recorded'}
        </StatusBadge>
        {!row.residency_recorded && (
          <span className="text-[12px] text-muted-foreground">
            {row.grant_blocked ? 'Grants refused' : 'Reported, not blocking'}
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * The GDPR half of the compliance view.
 *
 * Renders nothing when the org is not EU-designated. GDPR is not a roadmap marker:
 * an org with no EU residency need sees no GDPR heading, no empty state, and no
 * disabled tile.
 */
export function GdprSection({
  evidence,
}: {
  readonly evidence: GdprEvidence;
}): React.JSX.Element | null {
  if (!evidence.eu_designated) return null;

  const chapters: GdprControlRow['chapter'][] = ['transfers', 'security', 'accountability'];
  const download = (format: 'json' | 'csv'): void => {
    const stamp = evidence.generated_at.slice(0, 10);
    if (format === 'json') {
      downloadTextFile(
        `greenlight-gdpr-${stamp}.json`,
        buildGdprJson(evidence),
        'application/json',
      );
    } else {
      downloadTextFile(`greenlight-gdpr-${stamp}.csv`, buildGdprCsv(evidence), 'text/csv');
    }
  };

  return (
    <div className="space-y-5" data-testid="gdpr-section">
      <SectionCard title="GDPR">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Article coverage for this EU-designated organization. Coverage and evidence, not an
          attestation of GDPR compliance.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('json')}>
            Export GDPR JSON
          </Button>
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('csv')}>
            Export GDPR CSV
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Integrations">
        {evidence.integrations.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">
            No integrations registered yet, so there is no residency evidence to show.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {evidence.integrations.map((row) => (
              <IntegrationRow key={row.integration_id} row={row} />
            ))}
          </ul>
        )}
      </SectionCard>

      {chapters.map((chapter) => {
        const rows = evidence.controls.filter((c) => c.chapter === chapter);
        if (rows.length === 0) return null;
        return (
          <SectionCard key={chapter}>
            <Eyebrow>{CHAPTER_LABEL[chapter]}</Eyebrow>
            <ul className="mt-2 divide-y divide-border">
              {rows.map((row) => (
                <ControlRow key={row.control} row={row} />
              ))}
            </ul>
          </SectionCard>
        );
      })}
    </div>
  );
}
